import type { ClientError } from '@tribeplatform/gql-client/lib'
import type {
  MutationUpdateMemberNetworkNotificationSettingsArgs,
  MemberNetworkNotificationSettings,
  MemberNotificationSettings,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getMemberNotificationSettingsKey } from '../../utils/keys/notification.key.js'

export const useUpdateMemberNetworkNotificationSettings = (options?: {
  useMutationOptions?: UseMutationOptions<
    MemberNetworkNotificationSettings,
    ClientError,
    MutationUpdateMemberNetworkNotificationSettingsArgs,
    { snapshot: MemberNotificationSettings }
  >
}) => {
  const { useMutationOptions } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()
  const memberSettingsKey = getMemberNotificationSettingsKey()

  return useMutation<
    MemberNetworkNotificationSettings,
    ClientError,
    MutationUpdateMemberNetworkNotificationSettingsArgs,
    { snapshot: MemberNotificationSettings }
  >(variables => client.notifications.updateMemberNetworkSettings(variables), {
    onMutate: async newNetworkSettings => {
      await queryClient.cancelQueries(memberSettingsKey)

      const snapshot =
        queryClient.getQueryData<MemberNotificationSettings>(memberSettingsKey)

      queryClient.setQueriesData<MemberNotificationSettings>(
        memberSettingsKey,
        old => {
          return {
            ...old,
            network:
              old?.network?.map(n => {
                if (n?.channel === newNetworkSettings.channel) {
                  return {
                    ...n,
                    channel: newNetworkSettings.channel,
                    enabled: newNetworkSettings.input.enabled ?? n.enabled,
                    mention: newNetworkSettings.input.mention ?? n.mention,
                    reaction: newNetworkSettings.input.reaction ?? n.reaction,
                    sameAsDefault:
                      newNetworkSettings.input.sameAsDefault ?? n.sameAsDefault,
                  }
                }
                return n
              }) || [],
          }
        },
      )

      return { snapshot }
    },
    onError: (err, newNetworkSettings, context) => {
      // revert back the optimistic response
      queryClient.setQueriesData<MemberNotificationSettings>(
        memberSettingsKey,
        context.snapshot,
      )
    },
    onSettled: () => {
      // invalidate to refetch again
      queryClient.invalidateQueries(memberSettingsKey)
    },
    ...useMutationOptions,
  })
}
